/**
 * Work item #11 — persistance de l'écran courant dans l'URL.
 *
 * Synosight est une SPA sans router : l'écran actif était un simple `useState`
 * dans `App.jsx`, si bien qu'un CTRL+R repartait systématiquement sur Forecast.
 * Même parti pris que synolog (ticket #39) : on mappe la clé de vue sur un slug
 * d'URL et on pilote `history` à la main, sans embarquer react-router pour une
 * navigation à un seul niveau.
 *
 * Convention : slug = clé de vue, à l'identique. Contrairement à synolog il n'y
 * a pas d'écran d'accueil neutre — Forecast est une vue nommée dans la nav, donc
 * elle porte `/forecast` en clair ; `/` est un alias réécrit en `replaceState`
 * au montage. Ces slugs deviennent des URLs publiques (bookmarks, liens
 * partagés) : les considérer comme un contrat, ne pas les renommer même si la
 * clé interne bouge — ajouter un alias dans `LEGACY_SLUGS`.
 *
 * ⚠ Le `next` du SSO core est whitelisté côté API par
 * `SAFE_NEXT_PATTERN = /^\/[a-zA-Z0-9/_-]*$/` (synocore api/src/lib/oauthState.ts) :
 * tout slug doit rester dans cet alphabet. Le tiret est accepté, donc
 * `avant-vente` et `controle-gestion` passent tels quels.
 */

const FALLBACK_KEY = 'forecast';

// Anciens chemins éventuels → clé de vue : point d'extension quand un slug
// public doit être renommé sans casser les bookmarks.
const LEGACY_SLUGS = {
  // synotis-support#4 : l'écran unique `/admin` est découpé en une vue par
  // sujet ; l'ancien lien ouvre les tarifs, son premier onglet historique.
  admin: 'admin-rates',
};

/** Slug public d'une clé de vue. */
export function pathForViewKey(key) {
  return `/${key || FALLBACK_KEY}`;
}

/**
 * Clé de vue portée par un pathname, ou `null` si le chemin ne correspond à
 * aucune vue connue (racine, URL exotique, ancien lien) — l'appelant retombe
 * alors sur la vue de repli.
 *
 * @param {string} pathname                        ex. `/prospection`
 * @param {Set<string>|Map<string, unknown>} knownKeys clés de vue autorisées
 */
export function viewKeyFromPath(pathname, knownKeys) {
  const raw = (pathname || '/').replace(/\/+$/, '');
  if (raw === '' || raw === '/') return null;
  const slug = raw.startsWith('/') ? raw.slice(1) : raw;
  // Un slug à plusieurs segments n'est pas une vue (ex. asset servi en fallback).
  if (slug.includes('/')) return null;
  const key = LEGACY_SLUGS[slug] ?? slug;
  return knownKeys.has(key) ? key : null;
}

/**
 * Compat historique : avant #11, la vue d'ouverture se passait en query
 * (`?view=forecast&person=<id>`, lien « nouvel onglet » de la vue Direction).
 * Ces liens circulent déjà — on continue de les honorer, et `syncUrlToView`
 * retire ensuite le paramètre au profit du pathname.
 */
export function viewKeyFromLegacyQuery(search, knownKeys) {
  const raw = new URLSearchParams(search || '').get('view');
  if (!raw) return null;
  const key = LEGACY_SLUGS[raw] ?? raw;
  return knownKeys.has(key) ? key : null;
}

/**
 * Aligne l'URL sur la vue active sans recharger la page. `replace: true` pour
 * les corrections silencieuses (vue interdite, chemin inconnu, migration du
 * `?view=` legacy) afin de ne pas polluer l'historique avec une entrée que
 * l'utilisateur n'a jamais demandée.
 */
export function syncUrlToView(key, { replace = false } = {}) {
  const path = pathForViewKey(key);
  const params = new URLSearchParams(window.location.search);
  // `view` est désormais porté par le pathname : on ne le laisse pas traîner.
  params.delete('view');
  // `person` ne fait sens que sur Forecast (ForecastView le lit au montage).
  // Sans ce nettoyage, le filtre ressusciterait au refresh depuis une autre vue.
  if (key !== 'forecast') params.delete('person');
  const qs = params.toString();
  const url = path + (qs ? `?${qs}` : '') + window.location.hash;
  const current = window.location.pathname + window.location.search + window.location.hash;
  if (current === url && !replace) return;
  const method = replace ? 'replaceState' : 'pushState';
  window.history[method](null, '', url);
}

/**
 * Chemin à repasser en `next` au SSO pour revenir sur l'écran courant après
 * login. La query string est volontairement écartée : `safeNext` la rejetterait
 * et le retour retomberait sur `/`.
 */
export function currentPathForLogin() {
  return window.location.pathname || '/';
}
